import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { useIsDesktop } from '../hooks/useMediaQuery';

/**
 * プライバシーポリシーの本文。
 *
 * 原本は レフリアメモ/プライバシーポリシー全文.md。
 * 利用規約（TermsOfServicePage）から参照される関係にあるため、
 * 片方だけを直さないこと。
 */
const POLICY_LAST_UPDATED = '2026年8月28日';

const POLICY_SECTIONS: { title: string; body: string }[] = [
  {
    title: '1. 基本方針',
    body: [
      '当社は、利用者の個人情報の保護を最重要事項として認識し、個人情報保護法その他の関連法令を遵守し、適切な取扱いを行います。',
      '',
      '本サービスは、投稿者の匿名性を保護するため、投稿内容については匿名IDにより投稿者を特定できない形式で処理します。導入組織の管理者・システム管理者については、サービス運営のために必要な個人情報を収集・保存いたします。',
    ].join('\n'),
  },
  {
    title: '2. 収集する情報',
    body: [
      '当社は、以下の情報を収集する場合があります。',
      '',
      '【収集しない情報（投稿者として）】',
      '・氏名、住所、電話番号、メールアドレス等の個人を特定できる情報',
      '',
      '【収集される情報】',
      '・投稿内容（匿名IDにより投稿者を特定できない形式で処理）',
      '・投稿の統計情報（個人特定不可）',
      '・監査ログ（管理者の操作記録、セキュリティ目的）',
      '・接続した端末の情報（IPアドレス）',
      '　利用しているクラウドサービス（Google）側に一定期間だけ自動的に記録されます。システムの安定運用および不正利用の防止のために使用され、当社は投稿内容とIPアドレスを紐づけて管理することはありません',
      '',
      '【導入組織から取得する情報】',
      '・ご担当者名、部署、メールアドレス、電話番号、住所',
      '・契約・請求・お問い合わせ対応のために使用します',
      '・投稿内容と紐づけて管理することはありません',
      '',
      '【デバイス識別情報について】',
      '・端末管理のため、デバイス識別情報（フィンガープリント）を保存しています',
      '・導入組織単位での端末数制限の管理のみに使用されます',
      '・当社は投稿内容とデバイス識別情報を紐づけて管理することはありません',
    ].join('\n'),
  },
  {
    title: '3. 情報の利用目的',
    body: [
      '当社は、収集した情報を以下の目的で利用します。',
      '',
      '・本サービスの提供・運営',
      '・サービスの改善・開発',
      '・投稿の統計情報の作成（個人を特定できない形式）',
      '・システムの安定性向上',
      '・不正利用の防止',
      '・法令に基づく対応',
      '',
      '当社は、利用者の同意なく、上記の利用目的を超えて個人情報を利用することはありません。',
    ].join('\n'),
  },
  {
    title: '4. 匿名性を保護するための措置',
    body: [
      '本サービスは、投稿者の匿名性を保護するため、以下の措置を実施しています。',
      '',
      '【技術的保護措置】',
      '・匿名IDによる投稿者の匿名化（UUID + ハッシュ化により生成）',
      '・投稿内容と投稿者情報の分離管理',
      '・投稿内容とデバイス情報の分離管理',
      '・導入組織単位でのデータ分離（他の導入組織からの情報漏洩防止）',
      '・データベースアクセス制御の強化（Firestoreセキュリティルール）',
      '',
      '【監査ログについて】',
      '・管理者の操作記録として監査ログを保存しています',
      '・監査ログには管理者の操作内容、投稿ID、投稿内容のプレビューが含まれます',
      '・監査ログはセキュリティと不正利用防止の目的で使用されます',
      '・監査ログへのアクセスは管理者のみに限定されています',
      '',
      '【運営上の保護措置】',
      '・スタッフによる投稿者特定の禁止',
    ].join('\n'),
  },
  {
    title: '5. 情報の第三者提供',
    body: [
      '当社は、以下の場合を除き、利用者の個人情報を第三者に提供することはありません。',
      '',
      '・法令に基づく場合',
      '・人の生命、身体または財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき',
      '・公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難であるとき',
      '・国の機関もしくは地方公共団体またはその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき',
    ].join('\n'),
  },
  {
    title: '6. 情報の開示・訂正・削除',
    body: [
      '投稿者の氏名等、投稿者個人を直接特定する情報は原則として取得していません。',
      '',
      '当社が保有する情報に関する開示、訂正、利用停止等の請求については、個人情報保護法その他の関係法令に従い適切に対応します。',
    ].join('\n'),
  },
  {
    title: '7. クッキー・トラッキング技術',
    body: [
      '本サービスでは、以下の技術を使用する場合があります。',
      '',
      '【使用する技術】',
      '・セッション管理用のクッキー（ログイン情報の保持のため）',
      '・Firebaseサービス（認証・データ保存・セキュリティ目的）',
      '',
      '【使用しない技術】',
      '・個人を特定するクッキー',
      '・広告配信用のトラッキング',
      '・ソーシャルメディア連携',
      '・位置情報取得',
      '',
      '当社は、利用者のプライバシーを最優先に、必要最小限の技術のみを使用します。',
    ].join('\n'),
  },
  {
    title: '8. セキュリティ対策',
    body: [
      '当社は、利用者の情報を保護するため、以下のセキュリティ対策を実施しています。',
      '',
      '【技術的対策】',
      '・通信の暗号化（TLS/HTTPS）',
      '・データベースアクセス制御の強化（Firestoreセキュリティルール）',
      '・認証・認可の厳格な管理',
      '',
      '【物理的対策】',
      '・サーバーの物理的保護（クラウドサービス提供者のセキュリティ対策に依存）',
      '・アクセス権限の管理',
      '・入退室管理（クラウドサービス提供者のデータセンター）',
      '・監視カメラの設置（クラウドサービス提供者のデータセンター）',
      '',
      '【人的対策】',
      '・アクセス権限の最小化',
    ].join('\n'),
  },
  {
    title: '9. お問い合わせ',
    body: '本プライバシーポリシーに関するお問い合わせは、アプリ内の「ヘルプ」画面よりご連絡ください。',
  },
  {
    title: '10. 注意事項',
    body: '本プライバシーポリシーは予告なく変更される場合があります。改定後の内容は本ページに掲示した時点で効力を生じます。',
  },
];

export const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface }}>
      {/* ヘッダー */}
      <AppBar
        position="static"
        sx={{
          bgcolor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ color: 'black' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              color: 'black',
              fontWeight: 600,
            }}
          >
            プライバシーポリシー
          </Typography>
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box sx={{
        p: 2,
        maxWidth: isDesktop ? '800px' : '100%',
        mx: isDesktop ? 'auto' : 0,
      }}>
        {/* ヘッダーセクション */}
        <Box sx={{ mb: 3 }}>
          <Card
            sx={{
              bgcolor: `${AppColors.primary}20`,
              border: `1px solid ${AppColors.primary}50`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 'bold',
                  mb: 1,
                }}
              >
                プライバシーポリシー
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  mb: 1.5,
                }}
              >
                レフリア株式会社（以下「当社」）は、当社が提供するLiaVoice（以下「本サービス」）における利用者情報の取扱いについて、以下のとおり定めます。
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                }}
              >
                最終更新日: {POLICY_LAST_UPDATED}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* プライバシーポリシーコンテンツ */}
        <Card
          sx={{
            boxShadow: 2,
            border: `1px solid ${AppColors.border}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            {POLICY_SECTIONS.map((section) => (
              <Box key={section.title} sx={{ mb: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: AppColors.primary,
                    mb: 1,
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: AppColors.textPrimary,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {section.body}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* フッター */}
        <Box sx={{ mt: 3 }}>
          <Card
            sx={{
              bgcolor: '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                }}
              >
                © 2026 レフリア株式会社
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
